import { Input } from "antd";
import { setWidgetInSheetParams } from "../../../../appRedux/actions/Boards";
import { useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { useEffect, useState, useMemo, useRef } from "react";
import { Button, Message } from "../../../BP2Components";
import Select from "react-select";
import { ModeOption, Mode, ColumnMapping, SummariesValue } from "../types";
import { buildExternalSummaries } from "../utils";
import { getListV2 } from "../../../../services/api/Api";
import { getListV3 } from "../../../../services/restAPI/ListsV2";
import projectConfig from "@projectConfig";

type ColOption = { value: string; label: string };

const selectStyles = {
   menuPortal: (base) => ({ ...base, zIndex: 9999 }),
   control: (base) => ({ ...base, minHeight: "33px", height: "33px" }),
   valueContainer: (base) => ({ ...base, height: "33px", padding: "0 8px" }),
   input: (base) => ({ ...base, margin: "0px" }),
   indicatorsContainer: (base) => ({ ...base, height: "33px" }),
   option: (base, state) => ({
      ...base,
      minHeight: "32px",
      backgroundColor: state.isFocused
         ? projectConfig.cssVariables["--btn-primaryBackground"]
         : "transparent",
      color: state.isFocused ? projectConfig.cssVariables["--nav-background"] : "inherit",
      fontWeight: state.isSelected ? 600 : "normal",
   }),
};

export const ListSettings = ({ value }: { value: SummariesValue }) => {
   const [listId, setListId] = useState<string>(value.listId || "");
   const [mode, setMode] = useState<Mode>(value?.mode || "summary");
   const [columnMapping, setColumnMapping] = useState<ColumnMapping>(value?.columnMapping || {});
   const [fetchedColumns, setFetchedColumns] = useState<ColOption[]>([]);
   const [fetchedRows, setFetchedRows] = useState<Record<string, unknown>[]>([]);
   const [isLoadingColumns, setIsLoadingColumns] = useState(false);
   const currentRequestIdRef = useRef(0);
   const initialListIdRef = useRef(value.listId || "");
   const intl = useIntl();
   const dispatch = useDispatch();

   const isV3 = listId?.length > 10;

   const columnRoles: { key: keyof ColumnMapping; label: string }[] = useMemo(
      () => [
         { key: "id", label: intl.formatMessage({ id: "settings.columnMapping.id" }) },
         {
            key: "indicator_name",
            label: intl.formatMessage({ id: "settings.columnMapping.indicatorName" }),
         },
         {
            key: "indicator_prefix",
            label: intl.formatMessage({ id: "settings.columnMapping.indicatorPrefix" }),
         },
         {
            key: "indicator_value",
            label: intl.formatMessage({ id: "settings.columnMapping.indicatorValue" }),
         },
         {
            key: "indicator_suffix",
            label: intl.formatMessage({ id: "settings.columnMapping.indicatorSuffix" }),
         },
         {
            key: "indicator_completion_prefix",
            label: intl.formatMessage({ id: "settings.columnMapping.completionPrefix" }),
         },
         {
            key: "indicator_completion_value",
            label: intl.formatMessage({ id: "settings.columnMapping.completionValue" }),
         },
         {
            key: "indicator_completion_suffix",
            label: intl.formatMessage({ id: "settings.columnMapping.completionSuffix" }),
         },
         {
            key: "indicator_completion_percent",
            label: intl.formatMessage({ id: "settings.columnMapping.completionPercent" }),
         },
         {
            key: "indicator_board_link",
            label: intl.formatMessage({ id: "settings.columnMapping.boardLink" }),
         },
      ],
      [intl],
   );

   const modeOptions: ModeOption[] = useMemo(
      () => [
         { label: intl.formatMessage({ id: "settings.mode.summary" }), value: "summary" },
         { label: intl.formatMessage({ id: "settings.mode.external" }), value: "external" },
      ],
      [intl],
   );

   const selectedOption = modeOptions.find((opt) => opt.value === mode) || modeOptions[0];

   const handleSaveListSettings = () => {
      if (mode === "external") {
         if (!columnMapping.id || !columnMapping.indicator_name) {
            Message.error(intl.formatMessage({ id: "settings.columnMapping.requiredFields" }));
            return;
         }
      }
      const columnSummaries =
         mode === "external" && fetchedRows.length > 0
            ? buildExternalSummaries(fetchedRows, columnMapping)
            : [];

      dispatch(
         setWidgetInSheetParams({
            ...value,
            columnSummaries,
            listId,
            mode,
            columnMapping,
         }),
      );
   };

   const handleChangeMode = (option: ModeOption | null) => {
      if (option) setMode(option.value);
   };

   const handleChangeListId = (e: React.ChangeEvent<HTMLInputElement>) => {
      setListId(e.target.value);
      setFetchedColumns([]);
      setFetchedRows([]);
      setColumnMapping({});
   };

   const handleLoadColumns = async () => {
      const reqId = ++currentRequestIdRef.current;
      setIsLoadingColumns(true);
      try {
         const res = isV3 ? await getListV3(listId, 1, 1000) : await getListV2(listId, 1, 1000);
         const payload = isV3 ? res.data : res.data?.default;

         if (reqId !== currentRequestIdRef.current) return;

         if (
            res.status !== 200 ||
            !Array.isArray(payload?.columns) ||
            !Array.isArray(payload?.data)
         ) {
            Message.error(intl.formatMessage({ id: "error.loadingListData" }));
            return;
         }

         setFetchedColumns(payload.columns.map((col) => ({ value: col.label, label: col.label })));
         setFetchedRows(payload.data);
      } catch (e) {
         if (reqId !== currentRequestIdRef.current) return;
         Message.error(intl.formatMessage({ id: "error.loadingListData" }));
         console.error("Failed to load columns:", e);
      } finally {
         if (reqId === currentRequestIdRef.current) setIsLoadingColumns(false);
      }
   };

   const handleMappingChange = (key: keyof ColumnMapping, option: ColOption | null) => {
      setColumnMapping((prev) => ({
         ...prev,
         [key]: option?.value,
      }));
   };

   useEffect(() => {
      const shouldAutoloadInitialId =
         mode === "external" &&
         listId !== "" &&
         listId === initialListIdRef.current &&
         fetchedColumns.length === 0;

      if (shouldAutoloadInitialId) {
         void handleLoadColumns();
      }
   }, [mode, listId, fetchedColumns.length]);

   const showSaveButton = mode !== "external" || fetchedColumns.length > 0;
   const portalTarget = document.getElementById("dropdown-root") || undefined;

   return (
      <div className="summaries-settings">
         <label className="widget-settings__inner__label">
            {intl.formatMessage({ id: "settings.listId" })}:
            <Input onChange={handleChangeListId} value={listId} />
         </label>
         <label className="widget-settings__inner__label">
            {intl.formatMessage({ id: "settings.mode" })}:
            <Select
               options={modeOptions}
               value={selectedOption}
               onChange={handleChangeMode}
               isClearable={false}
               isSearchable={false}
               menuPortalTarget={portalTarget}
               menuPosition="fixed"
               styles={selectStyles}
            />
         </label>

         {mode === "external" && (
            <>
               <Button
                  onClick={handleLoadColumns}
                  buttonType="primary"
                  disabled={!listId || isLoadingColumns || fetchedColumns.length > 0}
               >
                  {isLoadingColumns
                     ? intl.formatMessage({ id: "global.loading" })
                     : intl.formatMessage({ id: "settings.loadColumns" })}
               </Button>

               {fetchedColumns.length > 0 && (
                  <div className="summaries-settings__column-mapping">
                     {columnRoles.map(({ key, label }) => (
                        <label key={key} className="widget-settings__inner__label">
                           {label}:
                           <Select
                              options={fetchedColumns}
                              value={
                                 fetchedColumns.find((o) => o.value === columnMapping[key]) || null
                              }
                              onChange={(option) => handleMappingChange(key, option)}
                              placeholder={intl.formatMessage({ id: "forms.chooseOption" })}
                              isClearable
                              isSearchable
                              menuPortalTarget={portalTarget}
                              menuPosition="fixed"
                              styles={selectStyles}
                           />
                        </label>
                     ))}
                  </div>
               )}
            </>
         )}

         {showSaveButton && (
            <Button onClick={handleSaveListSettings} buttonType="primary">
               {intl.formatMessage({ id: "global.preview" })}
            </Button>
         )}
      </div>
   );
};
